import type {
  FunnelStatus,
  FunnelStatusBulkUpdateBodyParams,
} from 'src/api/generated';

/**
 * Assigns reordered custom stages to existing custom slots, preserving protected
 * system positions. Authorized managers can reorder all stages using the same
 * slots. Drag-hook positions are temporary indices; compare persisted positions
 * by status ID when deciding which updates to send.
 * @param original - Current statuses and their persisted positions.
 * @param reordered - Status order proposed by the drag interaction.
 * @param canManageSystemStatuses - Whether system positions may change.
 * @returns Position-only updates for statuses whose position actually changed.
 */
export const buildFunnelStatusReorderUpdates = (
  original: FunnelStatus[],
  reordered: FunnelStatus[],
  canManageSystemStatuses: boolean
): FunnelStatusBulkUpdateBodyParams['updates'] => {
  const slots = [...original].sort((a, b) => a.position - b.position);
  const persistedPositions = new Map(
    original.map((status) => [status.id, status.position])
  );
  const movable = reordered.filter(
    (status) => canManageSystemStatuses || !status.systemKey
  );
  let index = 0;
  return slots.flatMap((slot) => {
    if (!canManageSystemStatuses && slot.systemKey) return [];
    const next = movable[index++];
    return persistedPositions.get(next.id) !== slot.position
      ? [{ id: next.id, data: { position: slot.position } }]
      : [];
  });
};
